import React, { useCallback } from 'react'
import { ActionButton, InputField, Modal } from '@enamelai/design-system'
import { Icon as CopyIcon } from '@enamelai/design-system/icons/Copy'
import { ISipCredentialsOnce } from '@dataTypes/clinic.type'
import './AddDeviceStyle.css'
import { createFailedToast } from '@utils/notificationToast.util'
import { useNotificationToast } from '@context/NotificationToastContext'

export type VoipConfigurationModalProps = {
  show: boolean
  onHide: () => void
  data: ISipCredentialsOnce
}

type CredentialFieldProps = {
  label: string
  value: string
  onCopy: () => void
}

const CredentialField = ({
  label,
  value,
  onCopy,
}: CredentialFieldProps): React.ReactElement => (
  <div className="scp-voip-credential">
    <InputField
      variant="text"
      fullWidth
      label={label}
      value={value}
      disabled
      clearable={false}
    />
    <ActionButton
      icon={<CopyIcon className="ds-action-button__svg" aria-hidden="true" />}
      label={`Copy ${label}`}
      tone="primary"
      variant="outline"
      onClick={onCopy}
    />
  </div>
)

const VoipConfigurationModal = ({
  show,
  onHide,
  data,
}: VoipConfigurationModalProps): React.ReactElement => {
  const { showToast } = useNotificationToast()
  const copyToClipboard = useCallback(
    async (value: string, label: string): Promise<void> => {
      try {
        await navigator.clipboard.writeText(value)
      } catch {
        showToast(
          createFailedToast('Error', `Could not copy ${label} to clipboard.`),
        )
      }
    },
    [],
  )

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="md"
      closeOnBackdrop={false}
      className="scp-add-device-modals"
      title="Configure your VoIP phone"
      description={[
        'The device has been added to our system.',
        'Kindly configure your VoIP phone using the following details. The password is shown only once and cannot be retrieved later.',
        "Please copy it before closing this window. You can regenerate it from the device's edit screen if needed.",
      ]}
      primaryLabel="I've saved these credentials"
      secondaryLabel=""
      onPrimaryClick={onHide}
    >
      <div className="scp-pm-grid">
        <CredentialField
          label="Username"
          value={data.sip_registration_username}
          onCopy={() =>
            void copyToClipboard(data.sip_registration_username, 'Username')
          }
        />
        <CredentialField
          label="Register name"
          value={data.sip_registration_username}
          onCopy={() =>
            void copyToClipboard(
              data.sip_registration_username,
              'Register name',
            )
          }
        />
        <CredentialField
          label="Password"
          value={data.password}
          onCopy={() => void copyToClipboard(data.password, 'Password')}
        />
        <CredentialField
          label="Server host"
          value={data.sip_domain_name}
          onCopy={() =>
            void copyToClipboard(data.sip_domain_name, 'Server host')
          }
        />
      </div>
      <CredentialField
        label="SIP URI"
        value={data.sip_uri}
        onCopy={() => void copyToClipboard(data.sip_uri, 'SIP URI')}
      />
      <span className="ds-modal__description">
        Setup tip: Point your handset&apos;s SIP account at the server host
        above using TCP/UDP on port 5060 (or TLS on 5061) with the username and
        password shown.
      </span>
    </Modal>
  )
}

export default VoipConfigurationModal
